
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useRolePermissions } from "@/components/role-manager/hooks/useRolePermissions";

interface UserRole {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  created_at: string;
}

export const useRoleManager = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [adminStatus, setAdminStatus] = useState<{isAdmin: boolean, email: string | null} | null>(null);
  
  const rolePermissions = useRolePermissions();
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data, error } = await supabase.rpc('auth_debug_function');
        if (error) throw error;
        
        if (data && data.length > 0) {
          setAdminStatus({
            isAdmin: isAdmin,
            email: data[0].current_email
          });
          
          fetchUserRolesWithEmail();
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
        toast.error("Failed to verify admin status");
      }
    };
    
    const fetchUserRolesWithEmail = async () => {
      try {
        const { data, error } = await supabase
          .rpc('get_user_roles_with_email');
          
        if (error) throw error;
        setUserRoles(data || []);
      } catch (error) {
        console.error("Error fetching user roles:", error);
        toast.error("Failed to load user roles");
      } finally {
        setLoading(false);
      }
    };
    
    checkAdminStatus();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin && !loading) {
      navigate("/");
    }
  }, [isAdmin, navigate, loading]);

  return {
    userRoles,
    loading,
    adminStatus,
    isAdmin,
    ...rolePermissions
  };
};
